import React from 'react';
import { motion } from 'framer-motion';
import { GlobeEuropeAfricaIcon, ShieldCheckIcon, PresentationChartLineIcon, TruckIcon, DocumentMagnifyingGlassIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const Procurement = () => {
    const fadeInUp = {
        hidden: { opacity: 0, y: 40 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
    };

    const procurementModel = [
        "Direct farm-level sourcing",
        "Quality grading & standardization",
        "Transparent pricing mechanism",
        "Bulk aggregation & supply management",
        "Market linkage & distribution support"
    ];

    const qualityApproach = [
        { title: "Scientific quality inspection", icon: DocumentMagnifyingGlassIcon },
        { title: "Moisture & grade verification", icon: ClipboardDocumentCheckIcon },
        { title: "Safe handling & logistics coordination", icon: TruckIcon },
        { title: "Timely payment assurance", icon: ShieldCheckIcon }
    ];

    const whyChooseSystem = [
        "Fair market access for farmers",
        "Reliable sourcing for buyers",
        "Reduced supply chain inefficiencies",
        "Higher transparency & trust",
        "Scalable agricultural trade model"
    ];

    return (
        <div className="bg-gray-50 min-h-screen font-sans">
            {/* Hero Section */}
            <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center bg-gray-900 text-white overflow-hidden">
                <motion.div
                    initial={{ scale: 1.1 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 10, repeat: Infinity, repeatType: "reverse" }}
                    className="absolute inset-0 w-full h-full"
                >
                    <div className="absolute inset-0 bg-black/60 z-10"></div>
                    <img
                        src={require('../../../assets/newimages/mas website product procurement.webp')}
                        alt="Agricultural Product Procurement"
                        className="w-full h-full object-cover opacity-80"
                    />
                </motion.div>

                <div className="relative z-20 text-center px-4 max-w-5xl mx-auto">
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-4xl md:text-6xl font-bold mb-6 leading-tight"
                    >
                        Agricultural Product <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500">Procurement</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="text-xl md:text-2xl text-gray-200 font-light"
                    >
                        Direct Sourcing. Fair Value. Reliable Supply.
                    </motion.p>
                </div>
            </section>

            <section className="py-20 px-4 md:px-8 max-w-7xl mx-auto">
                <div className="flex flex-col lg:flex-row gap-16 items-start">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={fadeInUp}
                        className="lg:w-1/2"
                    >
                        <p className="text-gray-600 text-lg leading-relaxed mb-6 font-light">
                            At <strong className="text-gray-900 font-bold">Marvelous Agri Solution</strong>, Agricultural Product Procurement connects farmers directly to structured markets with transparency, efficiency, and trust.
                        </p>
                        <p className="text-gray-600 text-lg leading-relaxed mb-8 font-light">
                            We streamline the sourcing process to ensure quality produce reaches the right buyers while farmers receive fair and competitive pricing.
                        </p>

                        <div className="bg-emerald-50 border-l-4 border-emerald-500 p-6 rounded-r-2xl mb-12">
                            <p className="text-xl font-medium text-emerald-900 italic">
                                Because strong supply chains build strong agricultural economies.
                            </p>
                        </div>

                        <h3 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                            Our Procurement Model
                        </h3>

                        <div className="space-y-4 mb-8">
                            {procurementModel.map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-white shadow-sm border border-gray-100 hover:border-emerald-300 hover:shadow-md transition-all">
                                    <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-800 font-medium text-lg">{item}</span>
                                </div>
                            ))}
                        </div>

                        <p className="text-emerald-800 font-medium italic mb-10 border-b border-gray-200 pb-6">
                            We reduce intermediaries and improve value realization for producers.
                        </p>

                        <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                            Why Choose Our Procurement System?
                        </h3>

                        <div className="mt-8 space-y-4">
                            {whyChooseSystem.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
                                    <CheckCircleIcon className="h-5 w-5 text-gray-400 flex-shrink-0" />
                                    <span className="text-gray-700 font-medium">{item}</span>
                                </div>
                            ))}
                        </div>

                    </motion.div>

                    <div className="lg:w-1/2 relative space-y-12">
                        <div className="grid grid-cols-2 gap-4 mb-8">
                            <img src={require('../../../assets/newimages/product procurement agri mas website.webp')} alt="Procurement 1" className="rounded-2xl w-full h-48 object-cover shadow-lg" />
                            <img src={require('../../../assets/newimages/product procurement mas website.webp')} alt="Procurement 2" className="rounded-2xl w-full h-48 object-cover shadow-lg translate-y-6" />
                        </div>

                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8 }}
                            className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100"
                        >
                            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                                Quality-Focused Approach
                            </h3>

                            <p className="text-gray-600 mb-8 font-light">
                                Every procurement cycle includes:
                            </p>

                            <div className="space-y-4 mb-8">
                                {qualityApproach.map((approach, idx) => (
                                    <div key={idx} className="flex items-center gap-4 p-3 hover:bg-emerald-50 rounded-xl transition-colors">
                                        <div className="bg-emerald-100 p-3 rounded-xl flex-shrink-0">
                                            <approach.icon className="w-5 h-5 text-emerald-600" />
                                        </div>
                                        <p className="text-gray-800 font-medium text-lg">{approach.title}</p>
                                    </div>
                                ))}
                            </div>

                            <div className="bg-gray-50 border-t-2 border-emerald-500 p-6 rounded-b-xl -mx-8 -mb-8 mt-8">
                                <p className="font-bold text-emerald-800 text-center italic">
                                    Consistency in quality creates consistency in demand.
                                </p>
                            </div>
                        </motion.div>

                    </div>
                </div>
            </section>
        </div>
    );
};

export default Procurement;
